import { Link } from 'react-router-dom'
import { Button } from '../ui/button'
import { useI18n } from '../../i18n'
import { usePlan } from '../../hooks/use-plan'
import { Crown, Zap, ArrowRight } from 'lucide-react'
import { useEffect } from 'react'

function formatTokens(n: number) {
  if (n >= 10000000) return `${(n / 10000000).toFixed(0)}千万`
  if (n >= 10000) return `${(n / 10000).toFixed(0)}万`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
  return `${n}`
}

export function CurrentPlanCard() {
  const { t } = useI18n()
  const { currentPlan, fetchCurrentPlan } = usePlan()

  useEffect(() => {
    fetchCurrentPlan()
  }, [])

  if (!currentPlan) {
    return (
      <div className="p-4 rounded-2xl bg-white border border-black/[0.04]">
        <p className="text-sm text-gray-400">{t('暂无套餐信息', 'No plan info')}</p>
        <Link to="/pricing">
          <Button variant="outline" className="mt-3 border-orange-200 text-orange-600 hover:bg-orange-50 h-8 text-xs rounded-xl">
            {t('选择套餐', 'Choose Plan')}
          </Button>
        </Link>
      </div>
    )
  }

  const usagePercent = currentPlan.tokenQuota > 0
    ? Math.min((currentPlan.tokenUsed / currentPlan.tokenQuota) * 100, 100)
    : 0

  const isExpired = currentPlan.expired

  return (
    <div className="p-4 rounded-2xl bg-white border border-black/[0.04]">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Crown className="h-4 w-4 text-orange-500" />
          <span className="text-sm font-medium text-[#1a1a1a]">
            {currentPlan.planName}
          </span>
          {isExpired && (
            <span className="text-[10px] text-red-500 bg-red-50 px-1.5 py-0.5 rounded-md">
              {t('已过期', 'Expired')}
            </span>
          )}
        </div>
        <Link to="/pricing">
          <Button variant="ghost" size="sm" className="h-7 text-xs text-orange-500 hover:text-orange-600 hover:bg-orange-50 px-2">
            {t('升级', 'Upgrade')} <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </Link>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">
            <Zap className="h-3 w-3 inline mr-1" />
            {t('Token 用量', 'Token Usage')}
          </span>
          <span className="text-gray-500">
            {formatTokens(currentPlan.tokenUsed)} / {formatTokens(currentPlan.tokenQuota)}
          </span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${usagePercent > 80 ? 'bg-red-500' : usagePercent > 50 ? 'bg-amber-500' : 'bg-orange-500'}`}
            style={{ width: `${usagePercent}%` }}
          />
        </div>
      </div>

      {currentPlan.planExpireTime && (
        <p className="text-[11px] text-gray-400 mt-2">
          {t(`到期日: ${currentPlan.planExpireTime.split('T')[0]}`, `Expires: ${currentPlan.planExpireTime.split('T')[0]}`)}
          {currentPlan.autoRenew === 1 && <span className="ml-1 text-emerald-500">{t('自动续费', 'Auto-renew')}</span>}
        </p>
      )}
    </div>
  )
}
